import { useEffect, useState, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
    Users,
    Search,
    Loader2,
    Mail,
    Phone,
    Calendar,
    X,
    ChevronRight,
} from "lucide-react";

const STATUS_OPTIONS = ["new", "in_review", "contacted", "closed"];
const STATUS_STYLES = {
    new: "bg-[#fdf6e8] text-[#8a6a1f] border-[#C8A24A]/30",
    in_review: "bg-[#e9efe5] text-[#0E2A47] border-[#0E2A47]/25",
    contacted: "bg-[#f0eef5] text-[#574a8a] border-[#574a8a]/25",
    closed: "bg-[#EFEBE0] text-[#6F6B63] border-[#E5DFD3]",
};

function ReferralDetail({ row, onClose, onUpdate }) {
    if (!row) return null;
    return (
        <div className="fixed inset-0 z-50 bg-[#1A2230]/40 backdrop-blur-sm" onClick={onClose} data-testid="referral-detail">
            <div
                className="absolute right-0 top-0 h-full w-full sm:w-[480px] bg-white shadow-2xl overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="sticky top-0 bg-white border-b border-[#E5DFD3] px-6 py-4 flex items-center justify-between">
                    <div>
                        <p className="text-[11px] uppercase tracking-[0.18em] text-[#C8A24A] font-medium">
                            Referral
                        </p>
                        <h3 className="font-[Outfit] text-[18px] font-semibold tracking-tight">
                            {row.youth_name || row.referrer_name}
                        </h3>
                    </div>
                    <button onClick={onClose} className="p-2 rounded-full hover:bg-[#EFEBE0]" data-testid="referral-detail-close">
                        <X className="h-4 w-4" />
                    </button>
                </div>
                <div className="p-6 space-y-5 text-[14px]">
                    <div>
                        <p className="rtc-label">Status</p>
                        <div className="flex flex-wrap gap-2">
                            {STATUS_OPTIONS.map((s) => (
                                <button
                                    key={s}
                                    onClick={() => onUpdate(row.id, s)}
                                    data-testid={`status-${s}`}
                                    className={`px-3 py-1.5 rounded-full text-[12.5px] font-medium border transition-colors ${
                                        row.status === s
                                            ? "bg-[#0E2A47] text-white border-[#0E2A47]"
                                            : "bg-white text-[#1A2230] border-[#E5DFD3] hover:border-[#0E2A47]"
                                    }`}
                                >
                                    {s.replace("_", " ")}
                                </button>
                            ))}
                        </div>
                    </div>
                    <Field label="Youth name" value={row.youth_name} />
                    <Field label="Youth age" value={row.youth_age} />
                    <Field label="Parent / guardian" value={row.parent_guardian} />
                    <Field label="Referrer name" value={row.referrer_name} />
                    <Field label="Referral source" value={row.referral_source} />
                    <Field label="Agency / Organization" value={row.agency_name} />
                    <Field label="Email" value={row.email} link={`mailto:${row.email}`} />
                    <Field label="Phone" value={row.phone} link={`tel:${row.phone}`} />
                    <Field label="Urgency" value={row.urgency_level} />
                    <Field label="Current concerns" value={row.current_concerns} multiline />
                    <Field label="Additional notes" value={row.additional_notes} multiline />
                    <Field
                        label="Received"
                        value={new Date(row.created_at).toLocaleString()}
                    />
                </div>
            </div>
        </div>
    );
}

const Field = ({ label, value, link, multiline }) => {
    if (value == null || value === "") return null;
    return (
        <div>
            <p className="text-[11.5px] uppercase tracking-wider text-[#6F6B63] mb-1">{label}</p>
            {link ? (
                <a href={link} className="text-[#0E2A47] hover:underline break-words">
                    {value}
                </a>
            ) : (
                <p className={`text-[#1A2230] ${multiline ? "whitespace-pre-wrap" : ""}`}>
                    {value}
                </p>
            )}
        </div>
    );
};

export default function AdminReferrals() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [q, setQ] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [selected, setSelected] = useState(null);
    const location = useLocation();

    // Auto-close drawer on route change / external nav
    useEffect(() => {
        setSelected(null);
    }, [location.pathname]);

    // Close drawer on Escape
    useEffect(() => {
        const onKey = (e) => {
            if (e.key === "Escape") setSelected(null);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, []);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/referral");
            setRows(data);
        } catch (e) {
            setError(formatApiError(e.response?.data?.detail) || e.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const updateStatus = async (id, status) => {
        try {
            const { data } = await api.patch(`/referral/${id}`, { status });
            setRows((rs) => rs.map((r) => (r.id === id ? data : r)));
            setSelected((s) => (s && s.id === id ? data : s));
            toast.success(`Marked as ${status.replace("_", " ")}`);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || e.message);
        }
    };

    const filtered = useMemo(() => {
        const ql = q.toLowerCase();
        return rows.filter((r) => {
            if (statusFilter !== "all" && r.status !== statusFilter) return false;
            if (!ql) return true;
            return (
                r.youth_name?.toLowerCase().includes(ql) ||
                r.referrer_name?.toLowerCase().includes(ql) ||
                r.email?.toLowerCase().includes(ql) ||
                r.agency_name?.toLowerCase().includes(ql) ||
                r.referral_source?.toLowerCase().includes(ql) ||
                r.phone?.includes(q)
            );
        });
    }, [rows, q, statusFilter]);

    return (
        <div className="space-y-6" data-testid="admin-referrals-page">
            <div className="flex items-end justify-between gap-4 flex-wrap">
                <div>
                    <p className="text-[12px] uppercase tracking-[0.2em] text-[#C8A24A] font-medium">
                        Admin
                    </p>
                    <h1 className="mt-2 font-[Outfit] text-3xl font-semibold tracking-tight">
                        Referrals
                    </h1>
                </div>
                <div className="flex items-center gap-2">
                    <div className="relative">
                        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[#8F8B83]" />
                        <input
                            placeholder="Search by name, email, county…"
                            className="rtc-input pl-10 w-72 max-w-[60vw]"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            data-testid="referrals-search"
                        />
                    </div>
                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="rtc-input w-40"
                        data-testid="referrals-status-filter"
                    >
                        <option value="all">All statuses</option>
                        {STATUS_OPTIONS.map((s) => (
                            <option key={s} value={s}>
                                {s.replace("_", " ")}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            {error && (
                <div className="rounded-xl bg-[#fdf6e8] border border-[#C8A24A]/30 text-[#8a6a1f] px-4 py-3 text-[13.5px]">
                    {error}
                </div>
            )}

            <div className="bg-white rounded-2xl border border-[#E5DFD3] overflow-hidden">
                {(() => {
                    if (loading) {
                        return (
                            <div className="p-12 text-center text-[#6F6B63] flex items-center justify-center gap-2">
                                <Loader2 className="h-4 w-4 animate-spin" /> Loading referrals…
                            </div>
                        );
                    }
                    if (filtered.length === 0) {
                        return (
                            <div className="p-12 text-center text-[#6F6B63]" data-testid="referrals-empty">
                                <Users className="h-8 w-8 mx-auto mb-3 text-[#8F8B83]" />
                                No referrals match your filters.
                            </div>
                        );
                    }
                    return (
                        <ul className="divide-y divide-[#E5DFD3]">
                            {filtered.map((r) => (
                                <li key={r.id}>
                                    <button
                                        onClick={() => setSelected(r)}
                                        className="w-full text-left p-5 hover:bg-[#F7F5F0] transition-colors flex items-start gap-4"
                                        data-testid={`referral-row-${r.id}`}
                                    >
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-3 mb-1.5">
                                                <p className="font-[Outfit] text-[16px] font-semibold truncate">
                                                    {r.youth_name || r.referrer_name}
                                                </p>
                                                <span
                                                    className={`text-[10.5px] uppercase tracking-wider font-medium px-2 py-0.5 rounded-full border ${
                                                        STATUS_STYLES[r.status] || STATUS_STYLES.new
                                                    }`}
                                                >
                                                    {r.status?.replace("_", " ") || "new"}
                                                </span>
                                                {r.urgency_level && (
                                                    <span className="text-[10.5px] uppercase tracking-wider font-medium px-2 py-0.5 rounded-full bg-[#F7ECC9] text-[#8a6a1f] border border-[#C8A24A]/30">
                                                        {r.urgency_level}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="flex items-center gap-4 text-[12.5px] text-[#6F6B63] flex-wrap">
                                                <span>By {r.referrer_name}</span>
                                                <span className="flex items-center gap-1.5"><Mail className="h-3 w-3" />{r.email}</span>
                                                {r.phone && <span className="flex items-center gap-1.5"><Phone className="h-3 w-3" />{r.phone}</span>}
                                                {r.agency_name && <span>· {r.agency_name}</span>}
                                                <span className="flex items-center gap-1.5"><Calendar className="h-3 w-3" />{new Date(r.created_at).toLocaleDateString()}</span>
                                            </div>
                                            {r.current_concerns && (
                                                <p className="mt-2 text-[13px] text-[#1A2230]/75 line-clamp-2">
                                                    {r.current_concerns}
                                                </p>
                                            )}
                                        </div>
                                        <ChevronRight className="h-4 w-4 text-[#8F8B83] mt-1 shrink-0" />
                                    </button>
                                </li>
                            ))}
                        </ul>
                    );
                })()}
            </div>

            <ReferralDetail row={selected} onClose={() => setSelected(null)} onUpdate={updateStatus} />
        </div>
    );
}
